import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import Stars from "@/components/Stars";

const STAT_CARDS = [
  { label: "Recent Form", value: "7/10", sub: "Cleared 27.5 points" },
  { label: "Opponent Matchup", value: "Top-8", sub: "Points allowed to SGs" },
  { label: "Usage Rate", value: "31.2%", sub: "Last 10 games" },
  { label: "Minutes Projection", value: "36 MIN", sub: "Competitive spread" },
];

export function AnalysisDemo({ onUnlock }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-24 lg:px-8" data-testid="analysis-demo-section">
      <SectionHeader
        eyebrow="Analysis Demo"
        title="We Show The Work."
        sub="This is what sits behind every Money Printer selection. Example shown with demo data."
        testid="analysis-demo-header"
      />

      <div className="grid gap-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.45 }}
          className="panel relative overflow-hidden p-6 sm:p-8 lg:col-span-5"
        >
          <div className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-[#00E676]/10 blur-3xl" aria-hidden="true" />
          <span className="tag border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">VIP · Player Prop</span>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-wider text-slate-500">Minnesota @ Oklahoma City</p>
          <h3 className="mt-1 font-display text-4xl font-bold uppercase leading-none text-white">Anthony Edwards</h3>
          <p className="mt-1 font-display text-2xl font-semibold uppercase text-[#00E676]">Over 27.5 Points</p>
          <div className="mt-4 flex items-center gap-3 font-mono text-xs text-slate-400">
            <span>Odds <span className="font-bold text-white">1.90</span></span>
            <span className="text-slate-600">·</span>
            <span>Stake <span className="font-bold text-white">1U</span></span>
            <span className="text-slate-600">·</span>
            <Stars value={4} />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {STAT_CARDS.map((s) => (
              <div key={s.label} className="rounded-lg border border-[#1E2738] bg-[#141A26] p-4">
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{s.label}</p>
                <p className="mt-1.5 font-mono text-xl font-bold text-[#00E676]">{s.value}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{s.sub}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="card-mp relative overflow-hidden p-6 sm:p-8 lg:col-span-7"
        >
          <p className="eyebrow">Full Written Analysis</p>
          <div className="paywall-fade mt-4 space-y-4 text-sm leading-relaxed text-slate-300 sm:text-base">
            <p>
              Edwards has cleared this number in 7 of his last 10 games, averaging 29.8 points on 31.2% usage over
              that stretch. OKC's defensive scheme funnels creation toward high-usage wings, and Minnesota's
              secondary shot creation is thin enough that late-clock possessions consistently return to Ant.
            </p>
            <p>
              The minutes projection matters here: with a 3.5-point spread the game profiles as competitive
              throughout, keeping Edwards on the floor for 36+ minutes. Books opened this prop at 28.5 and it
              steamed down a full point — a signal the market agrees with the direction, while still leaving
              measurable edge at 27.5.
            </p>
            <p className="blur-[3px] select-none" aria-hidden="true">
              Historical matchup data against OKC's point-of-attack coverage shows a 14% lift in self-created
              shot quality for primary initiators, and Edwards' free-throw rate in this spot ranks in the 88th
              percentile. Line movement, injury context and full projection tables continue inside VIP.
            </p>
          </div>
          <button
            data-testid="unlock-vip-analysis-btn"
            onClick={onUnlock}
            className="mt-6 inline-flex items-center gap-2 rounded-lg border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-[#F59E0B] transition-colors hover:bg-[#F59E0B]/20"
          >
            <Lock size={15} /> Unlock VIP Analysis
          </button>
        </motion.div>
      </div>
    </section>
  );
}

export default AnalysisDemo;
